import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, Loader2, Mail, Plus, RotateCcw, Save, Search, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { db } from "@/lib/billing";
import { defaultEmailTemplates, renderTemplateString, sampleVariables, TEMPLATE_VARIABLES, type DefaultEmailTemplate } from "@/lib/templateDefaults";
import { safeTemplateHTML } from "@/lib/safeHtml";
import { useCompany } from "@/hooks/useCompany";

type EmailTemplate = DefaultEmailTemplate & {
  id?: string;
  company_id?: string | null;
  from_name?: string;
  from_email?: string;
  preview_variables?: Record<string, string>;
  category?: string | null;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
};

const SENDER_DOMAIN = "delapresse.fr";
const DEFAULT_SENDER_EMAIL = "info@delapresse.fr";
const DEFAULT_SMTP_HOST = "delapresse.fr";
const DEFAULT_SMTP_PORT = "465";

async function fetchTemplates(companyId: string | null, isPlatformOwner: boolean): Promise<EmailTemplate[]> {
  if (!companyId && !isPlatformOwner) return [];
  let query = db.from("email_templates").select("*").order("email_type").order("language");
  if (companyId) query = query.eq("company_id", companyId);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

function payloadFor(template: EmailTemplate, companyId: string | null) {
  return {
    template_key: template.template_key,
    name: template.name,
    email_type: template.email_type,
    language: template.language,
    subject: template.subject,
    from_name: template.from_name || "Delapresse",
    from_email: template.from_email || "info@delapresse.fr",
    html_body: template.html_body,
    plain_text_body: template.plain_text_body,
    variables: template.variables ?? [],
    preview_variables: template.preview_variables ?? sampleVariables,
    category: template.category ?? "transactional",
    description: template.description ?? null,
    is_enabled: template.is_enabled,
    company_id: template.company_id ?? companyId,
  };
}

async function invokeFunction<T = any>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let message = error.message;
    const context = (error as any).context;
    if (context && typeof context.json === "function") {
      try {
        const payload = await context.json();
        message = payload?.error ?? payload?.message ?? message;
      } catch {
        // Keep the Supabase error message when the body is not JSON.
      }
    }
    throw new Error(message);
  }
  return data as T;
}

export default function AdminEmailTemplatesPage() {
  const queryClient = useQueryClient();
  const { currentCompanyId, isPlatformOwner, currentCompany } = useCompany();
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [preview, setPreview] = useState(false);
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [senderForm, setSenderForm] = useState({
    fromName: "Delapresse",
    fromEmail: DEFAULT_SENDER_EMAIL,
    username: DEFAULT_SENDER_EMAIL,
    password: "",
  });
  const [resendForm, setResendForm] = useState({
    apiKey: "",
    senderEmail: DEFAULT_SENDER_EMAIL,
  });

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["email-templates", currentCompanyId, isPlatformOwner],
    queryFn: () => fetchTemplates(currentCompanyId, isPlatformOwner),
    enabled: !!currentCompanyId || isPlatformOwner,
  });

  const { data: senderStatus } = useQuery({
    queryKey: ["email-sender-status", currentCompanyId],
    enabled: !!currentCompanyId,
    queryFn: async () => {
      const data = await invokeFunction<{ integrations?: Array<{
        provider: string;
        status: string;
        credentials?: Record<string, { configured?: boolean; preview?: string | null }>;
        updated_at?: string;
      }> }>("company-integrations-manager", { action: "list", company_id: currentCompanyId, environment: "live" });
      const integrations = (data?.integrations ?? []) as Array<{
        provider: string;
        status: string;
        credentials?: Record<string, { configured?: boolean; preview?: string | null }>;
        updated_at?: string;
      }>;
      return {
        smtp: integrations.find((item) => item.provider === "smtp") ?? null,
        resend: integrations.find((item) => item.provider === "resend") ?? null,
      };
    },
  });

  const types = Array.from(new Set(templates.map((template) => template.email_type))).sort();
  const filtered = useMemo(() => templates.filter((template) => {
    const q = search.toLowerCase();
    const matchesSearch = [template.name, template.email_type, template.subject, template.template_key, template.from_email].join(" ").toLowerCase().includes(q);
    return matchesSearch
      && (language === "all" || template.language === language)
      && (type === "all" || template.email_type === type)
      && (status === "all" || (status === "active" ? template.is_enabled : !template.is_enabled));
  }), [templates, search, language, type, status]);
  const active = selected ?? filtered[0] ?? null;
  const previewVars = { ...sampleVariables, ...(active?.preview_variables ?? {}), company_name: currentCompany?.name ?? sampleVariables.company_name };
  const smtpStatus = senderStatus?.smtp ?? null;
  const resendStatus = senderStatus?.resend ?? null;
  const senderMetadata = smtpStatus?.credentials ?? {};
  const resendMetadata = resendStatus?.credentials ?? {};
  const senderEmailPreview = senderMetadata.sender_email?.preview ?? DEFAULT_SENDER_EMAIL;
  const senderUserPreview = senderMetadata.username?.preview ?? senderMetadata.api_key?.preview ?? null;
  const senderHostPreview = senderMetadata.smtp_host?.preview ?? DEFAULT_SMTP_HOST;
  const senderDomain = String(senderMetadata.sender_domain?.preview ?? SENDER_DOMAIN);
  const smtpConfigured = ["configured", "connected"].includes(String(smtpStatus?.status ?? ""));
  const resendConfigured = ["configured", "connected"].includes(String(resendStatus?.status ?? ""));
  const senderVerified = resendConfigured || smtpConfigured;
  const resendKeyPreview = resendMetadata.api_key?.preview ?? null;
  const activeProvider = resendConfigured ? "Resend" : "SMTP";

  const saveResend = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error("Select a company first.");
      const apiKey = resendForm.apiKey.trim();
      if (!apiKey) throw new Error("Enter a Resend API key first.");
      await invokeFunction("company-integrations-manager", {
        action: "save",
        company_id: currentCompanyId,
        provider: "resend",
        environment: "live",
        credentials: {
          api_key: apiKey,
          sender_email: resendForm.senderEmail.trim() || DEFAULT_SENDER_EMAIL,
          sender_domain: (resendForm.senderEmail.split("@")[1] || SENDER_DOMAIN).trim(),
        },
        status: "configured",
      });
    },
    onSuccess: () => {
      setResendForm((value) => ({ ...value, apiKey: "" }));
      queryClient.invalidateQueries({ queryKey: ["email-sender-status"] });
      toast.success("Resend sender saved");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save Resend sender"),
  });

  const testResend = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error("Select a company first.");
      if (resendForm.apiKey.trim()) await saveResend.mutateAsync();
      const data = await invokeFunction<{ ok?: boolean; status?: string; error?: string; message?: string }>("company-integrations-manager", {
        action: "test",
        company_id: currentCompanyId,
        provider: "resend",
        environment: "live",
      });
      if (!data?.ok) throw new Error(data?.error ?? data?.message ?? "Resend test failed");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-sender-status"] });
      toast.success("Resend connection verified");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not verify Resend"),
  });

  const saveSender = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error("Select a company first.");
      const password = senderForm.password.trim();
      const fromName = senderForm.fromName.trim() || "Delapresse";
      const fromEmail = senderForm.fromEmail.trim() || DEFAULT_SENDER_EMAIL;
      const credentials: Record<string, string> = {
        sender_name: fromName,
        sender_email: fromEmail,
        sender_domain: (fromEmail.split("@")[1] || SENDER_DOMAIN).trim(),
        smtp_host: DEFAULT_SMTP_HOST,
        smtp_port: DEFAULT_SMTP_PORT,
        username: senderForm.username.trim(),
      };
      if (password) {
        credentials.password = password;
      }
      await invokeFunction("company-integrations-manager", {
        action: "save",
        company_id: currentCompanyId,
        provider: "smtp",
        environment: "live",
        credentials,
        status: "configured",
      });
      const { error: templateError } = await db
        .from("email_templates")
        .update({ from_name: fromName, from_email: fromEmail })
        .eq("company_id", currentCompanyId);
      if (templateError) throw templateError;
    },
    onSuccess: () => {
      setSenderForm((value) => ({ ...value, password: "" }));
      queryClient.invalidateQueries({ queryKey: ["email-sender-status"] });
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      toast.success("Email sender settings saved");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save sender settings"),
  });

  const testSender = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error("Select a company first.");
      if (senderForm.password.trim()) await saveSender.mutateAsync();
      const data = await invokeFunction<{ ok?: boolean; status?: string; message?: string; error?: string }>("company-integrations-manager", {
        action: "test",
        company_id: currentCompanyId,
        provider: "smtp",
        environment: "live",
      });
      if (!data?.ok) throw new Error(data?.error ?? "Connection test failed");
      return data as { status?: string; message?: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["email-sender-status"] });
      if (data?.status === "connected") {
        toast.success("Email sender connection verified");
      } else {
        toast.message(data?.message ?? "SMTP settings saved; delivery verification is still pending.");
      }
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not verify sender connection"),
  });

  const save = useMutation({
    mutationFn: async (template: EmailTemplate) => {
      const payload = payloadFor(template, currentCompanyId);
      const result = template.id
        ? await db.from("email_templates").update(payload).eq("id", template.id)
        : await db.from("email_templates").insert(payload);
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      setSelected(null);
      toast.success("Email template saved");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save template"),
  });

  const seedDefaults = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error("Select a company first.");
      const { error } = await db.rpc("seed_company_default_templates", {
        _company_id: currentCompanyId,
        _company_name: currentCompany?.name ?? "Delapresse",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      toast.success("Default Delapresse templates restored");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not restore default templates"),
  });

  const remove = useMutation({
    mutationFn: async (template: EmailTemplate) => {
      if (!template.id) throw new Error("Template must be saved before deletion.");
      const { error } = await db.from("email_templates").delete().eq("id", template.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      setSelected(null);
      toast.success("Email template deleted");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not delete template"),
  });

  const testSend = useMutation({
    mutationFn: async (template: EmailTemplate) => {
      if (!template.id) throw new Error("Save this template before sending a test.");
      const { data: current } = await supabase.auth.getUser();
      const recipient = current?.user?.email;
      if (!recipient) throw new Error("No admin email is available for test send.");
      const data = await invokeFunction<{ ok?: boolean; status?: string; error?: string; message?: string }>("send-template-email", {
        template_id: template.id,
        recipient_email: recipient,
        variables: previewVars,
      });
      if (!data?.ok) throw new Error(data?.error ?? data?.message ?? "Test email was not sent");
      return data;
    },
    onSuccess: () => toast.success("Test email requested"),
    onError: (err: any) => toast.error(err?.message ?? "Could not send test email"),
  });

  const patch = (changes: Partial<EmailTemplate>) => setSelected({ ...(active as EmailTemplate), ...changes });
  const duplicate = (template: EmailTemplate) => setSelected({
    ...template,
    id: undefined,
    template_key: `${template.template_key}_copy_${Date.now()}`,
    name: `${template.name} copy`,
    is_enabled: false,
  });
  const createNew = () => setSelected({
    ...defaultEmailTemplates[0],
    id: undefined,
    template_key: `custom_email_${Date.now()}`,
    name: "Custom email template",
    company_id: currentCompanyId,
    from_name: "Delapresse",
    from_email: "info@delapresse.fr",
    preview_variables: sampleVariables,
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Email Templates</h1>
          <p className="text-xs text-muted-foreground">Company-managed templates used by auth, billing, marketing, and system notifications.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => seedDefaults.mutate()} disabled={seedDefaults.isPending || !currentCompanyId}>
            {seedDefaults.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            Restore defaults
          </Button>
          <Button variant="outline" size="sm" onClick={createNew}><Plus className="mr-2 h-4 w-4" />New</Button>
          {active && <Button size="sm" onClick={() => save.mutate(active)} disabled={save.isPending}>{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button>}
        </div>
      </header>
      <main className="grid gap-6 p-6 lg:grid-cols-[390px_1fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="gap-3">
              <CardTitle className="text-base">Email sender</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span>Status</span>
                <Badge variant={senderVerified ? "default" : "secondary"}>{senderVerified ? `${activeProvider} ready` : "Pending"}</Badge>
              </div>
              <div className="rounded-md border border-border p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">Resend</div>
                    <div className="text-xs text-muted-foreground">Preferred for template tests and app-managed emails.</div>
                  </div>
                  <Badge variant={resendConfigured ? "default" : "secondary"}>{resendConfigured ? "On" : "Off"}</Badge>
                </div>
                <div className="grid gap-3">
                  <Field label="Resend sender email">
                    <Input value={resendForm.senderEmail} onChange={(event) => setResendForm((value) => ({ ...value, senderEmail: event.target.value }))} />
                  </Field>
                  <Field label="Resend API key">
                    <Input type="password" autoComplete="new-password" placeholder={resendKeyPreview ? "Stored securely - enter a new key to replace it" : "re_..."} value={resendForm.apiKey} onChange={(event) => setResendForm((value) => ({ ...value, apiKey: event.target.value }))} />
                  </Field>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => testResend.mutate()} disabled={testResend.isPending || saveResend.isPending || !currentCompanyId}>
                    {testResend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    Test Resend
                  </Button>
                  <Button size="sm" onClick={() => saveResend.mutate()} disabled={saveResend.isPending || !currentCompanyId}>
                    {saveResend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Resend
                  </Button>
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">SMTP fallback</div>
                    <div className="text-xs text-muted-foreground">Used when no Resend key is available.</div>
                  </div>
                  <Badge variant={smtpConfigured ? "default" : "secondary"}>{smtpConfigured ? "On" : "Off"}</Badge>
                </div>
              <div className="grid gap-3">
                <Field label="From name">
                  <Input value={senderForm.fromName} onChange={(event) => setSenderForm((value) => ({ ...value, fromName: event.target.value }))} />
                </Field>
                <Field label="From email">
                  <Input value={senderForm.fromEmail} onChange={(event) => setSenderForm((value) => ({ ...value, fromEmail: event.target.value, username: value.username === DEFAULT_SENDER_EMAIL ? event.target.value : value.username }))} />
                </Field>
                <Field label="Email username">
                  <Input value={senderForm.username} onChange={(event) => setSenderForm((value) => ({ ...value, username: event.target.value }))} />
                </Field>
                <Field label="Password">
                  <Input type="password" autoComplete="new-password" placeholder={senderUserPreview ? "Stored securely - enter a new password to replace it" : "Email password or SMTP secret"} value={senderForm.password} onChange={(event) => setSenderForm((value) => ({ ...value, password: event.target.value }))} />
                </Field>
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                Current: {senderEmailPreview} via {senderHostPreview}
                {senderUserPreview ? ` - username ${senderUserPreview}` : ""}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => testSender.mutate()} disabled={testSender.isPending || saveSender.isPending || !currentCompanyId}>
                  {testSender.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Test connection
                </Button>
                <Button size="sm" onClick={() => saveSender.mutate()} disabled={saveSender.isPending || !currentCompanyId}>
                  {saveSender.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save sender
                </Button>
              </div>
              </div>
              <p className="text-xs text-muted-foreground">Runtime order: Resend first when configured, then SMTP/Supabase Auth fallback. Domain: {senderDomain || SENDER_DOMAIN}.</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="gap-3">
              <CardTitle className="text-base">Templates</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search templates..." value={search} onChange={(event) => setSearch(event.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Select value={language} onValueChange={setLanguage}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All languages</SelectItem><SelectItem value="fr">FR</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent></Select>
                <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem>{types.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select>
                <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : filtered.length ? filtered.map((template) => (
                <button key={template.id ?? template.template_key} className="w-full rounded-md border border-border p-3 text-left hover:bg-accent" onClick={() => { setSelected(template); setPreview(false); }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{template.name}</span>
                    <Badge variant={template.is_enabled ? "default" : "secondary"}>{template.is_enabled ? "On" : "Off"}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{template.email_type} - {template.language} - {template.from_email}</div>
                </button>
              )) : (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No templates. Restore defaults to seed Delapresse templates for this company.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {active && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{active.name}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => duplicate(active)}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
                <Button variant="outline" size="sm" onClick={() => setPreview(!preview)}><Eye className="mr-2 h-4 w-4" />Preview</Button>
                <Button variant="outline" size="sm" onClick={() => testSend.mutate(active)} disabled={testSend.isPending}>{testSend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Test</Button>
                <Button variant="outline" size="sm" onClick={() => remove.mutate(active)} disabled={!active.id || remove.isPending}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Key"><Input value={active.template_key} onChange={(event) => patch({ template_key: event.target.value })} disabled={Boolean(active.id)} /></Field>
                <Field label="Template name"><Input value={active.name} onChange={(event) => patch({ name: event.target.value })} /></Field>
                <Field label="Type"><Input value={active.email_type} onChange={(event) => patch({ email_type: event.target.value })} /></Field>
                <Field label="Language"><Select value={active.language} onValueChange={(value: "fr" | "en") => patch({ language: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fr">French</SelectItem><SelectItem value="en">English</SelectItem></SelectContent></Select></Field>
                <Field label="From name"><Input value={active.from_name ?? "Delapresse"} onChange={(event) => patch({ from_name: event.target.value })} /></Field>
                <Field label="From email"><Input value={active.from_email ?? "info@delapresse.fr"} onChange={(event) => patch({ from_email: event.target.value })} /></Field>
                <Field label="Subject"><Input value={active.subject} onChange={(event) => patch({ subject: event.target.value })} /></Field>
                <Field label="Category"><Input value={active.category ?? ""} onChange={(event) => patch({ category: event.target.value })} /></Field>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Active</Label><Switch checked={active.is_enabled} onCheckedChange={(checked) => patch({ is_enabled: checked })} /></div>
              <div className="flex flex-wrap gap-2">{TEMPLATE_VARIABLES.map((v) => <Badge key={v} variant="outline">{`{{${v}}}`}</Badge>)}</div>
              {preview ? (
                <div className="rounded-md border border-border p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4" />{renderTemplateString(active.subject, previewVars)}</div>
                  <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: safeTemplateHTML(renderTemplateString(active.html_body, previewVars)) }} />
                </div>
              ) : (
                <>
                  <Field label="HTML body"><Textarea className="min-h-48 font-mono text-xs" value={active.html_body} onChange={(event) => patch({ html_body: event.target.value })} /></Field>
                  <Field label="Plain text body"><Textarea className="min-h-32 font-mono text-xs" value={active.plain_text_body} onChange={(event) => patch({ plain_text_body: event.target.value })} /></Field>
                  <Field label="Preview variables JSON"><Textarea className="min-h-28 font-mono text-xs" value={JSON.stringify(active.preview_variables ?? sampleVariables, null, 2)} onChange={(event) => {
                    try { patch({ preview_variables: JSON.parse(event.target.value) }); } catch { patch({ preview_variables: active.preview_variables }); }
                  }} /></Field>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
